import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Car, Tent, Armchair, Play, Pause } from "lucide-react";

const MODES = {
  parking: { label: "Parking", icon: Car, zone: "Asphalt Bay 12", seeking: "shade" },
  camping: { label: "Camping", icon: Tent, zone: "Pine Ridge Pitch 7", seeking: "shade" },
  beach: { label: "Sunbathing", icon: Armchair, zone: "South Beach Lounger", seeking: "sun" },
};

export default function Simulator() {
  const [hour, setHour] = useState(10);
  const [mode, setMode] = useState("parking");
  const [auto, setAuto] = useState(true);

  const t = (hour - 6) / 12;
  const s = Math.max(0, Math.sin(Math.PI * t));
  const elev = Math.round(s * 68);
  const shadowLen = 24 + (1 - s) * 150;
  const dir = hour <= 13 ? 1 : -1;
  const sunX = 60 + t * 480;
  const sunY = 205 - s * 160;
  const seeking = MODES[mode].seeking;
  const accent = seeking === "shade" ? "var(--shade)" : "var(--sun)";
  const comfort = Math.round((seeking === "shade" ? 1 - s : s) * 100);

  const fmt = `${String(Math.floor(hour)).padStart(2, "0")}:${hour % 1 >= 0.5 ? "30" : "00"}`;

  const sceneRef = useRef(null);
  const dragging = useRef(false);

  const moveSunFromEvent = (e) => {
    const svg = sceneRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 600;
    const tt = Math.min(1, Math.max(0, (x - 60) / 480));
    setHour(Math.round((6 + tt * 12) * 2) / 2);
  };
  const onScenePointerDown = (e) => {
    setAuto(false);
    dragging.current = true;
    if (e.currentTarget.setPointerCapture) e.currentTarget.setPointerCapture(e.pointerId);
    moveSunFromEvent(e);
  };
  const onScenePointerMove = (e) => {
    if (dragging.current) moveSunFromEvent(e);
  };
  const endDrag = () => {
    dragging.current = false;
  };

  useEffect(() => {
    if (!auto) return;
    const id = setInterval(() => {
      setHour((h) => (h + 0.5 > 18 ? 6 : h + 0.5));
    }, 500);
    return () => clearInterval(id);
  }, [auto]);

  return (
    <section
      id="simulator"
      data-testid="simulator-section"
      className="border-b border-[var(--sp-line)] bg-[var(--sp-surface)]"
    >
      <div className="mx-auto max-w-[1500px] px-5 py-20 sm:px-8 lg:py-28">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-4 flex items-center gap-3 font-mono2 text-xs uppercase tracking-[0.28em] text-[var(--sun)]"
        >
          <span className="h-px w-10 bg-[var(--sun)]" />
          Interactive Preview // 07
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl font-display text-2xl font-extrabold uppercase leading-none tracking-tight sm:text-4xl lg:text-5xl"
        >
          Drag the sun. Watch the shade move.
        </motion.h2>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 border border-[var(--sp-line)] bg-[var(--sp-bg)]"
        >
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[var(--sp-line)] px-5 py-4">
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Comfort zone mode">
              {Object.entries(MODES).map(([key, m]) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={mode === key}
                  data-testid={`sim-tab-${key}`}
                  onClick={() => setMode(key)}
                  className={`flex items-center gap-2 px-4 py-2.5 font-mono2 text-[10px] font-bold uppercase tracking-[0.14em] transition-colors duration-300 ${
                    mode === key
                      ? "bg-[var(--sun)] text-black"
                      : "border border-[var(--sp-line)] text-[var(--sp-muted)] hover:border-[var(--sun)] hover:text-[var(--sun)]"
                  }`}
                >
                  <m.icon className="h-3.5 w-3.5" />
                  {m.label}
                </button>
              ))}
            </div>
            <p className="font-mono2 text-[10px] uppercase tracking-[0.14em] text-[var(--sp-faint)]">
              {MODES[mode].zone}
            </p>
          </div>

          <div className="px-3 py-4 sm:px-5">
            <div className="relative mx-auto w-full max-w-[680px]">
              <svg
                ref={sceneRef}
                viewBox="0 0 600 250"
                onPointerDown={onScenePointerDown}
                onPointerMove={onScenePointerMove}
                onPointerUp={endDrag}
                onPointerLeave={endDrag}
                style={{ touchAction: "none" }}
                className="block w-full cursor-grab select-none active:cursor-grabbing"
                data-testid="simulator-scene"
                role="slider"
                aria-label="Drag to move the sun across the sky"
                aria-valuemin={6}
                aria-valuemax={18}
                aria-valuenow={hour}
              >
                <rect x="0" y="0" width="600" height="250" fill="transparent" />
                <line x1="0" y1="230" x2="600" y2="230" stroke="var(--sp-line)" strokeWidth="2" />
                <circle cx={sunX} cy={sunY} r="24" fill="var(--sun)" opacity="0.14" />
                <circle cx={sunX} cy={sunY} r="16" fill="var(--sun)" />
                <circle cx={sunX} cy={sunY} r="26" fill="none" stroke="var(--sun)" strokeWidth="1" strokeDasharray="3 5" opacity="0.6" />
                <polygon
                  points={`290,230 330,230 ${330 + dir * shadowLen},238 ${290 + dir * shadowLen},238`}
                  fill={seeking === "shade" ? "var(--shade)" : "var(--sun)"}
                  opacity={seeking === "shade" ? 0.55 : 0.35}
                />
                {mode === "parking" && (
                  <g>
                    <rect x="288" y="196" width="44" height="34" fill="var(--sp-fg)" />
                    <rect x="296" y="184" width="28" height="14" fill="var(--sp-fg)" />
                    <circle cx="299" cy="230" r="6" fill="var(--sp-bg)" stroke="var(--sp-fg)" strokeWidth="2" />
                    <circle cx="321" cy="230" r="6" fill="var(--sp-bg)" stroke="var(--sp-fg)" strokeWidth="2" />
                  </g>
                )}
                {mode === "camping" && (
                  <g>
                    <polygon points="310,150 280,230 340,230" fill="var(--sp-fg)" />
                    <rect x="306" y="230" width="8" height="10" fill="var(--sp-fg)" />
                  </g>
                )}
                {mode === "beach" && (
                  <g>
                    <rect x="286" y="222" width="48" height="8" fill="var(--sp-fg)" />
                    <rect x="322" y="200" width="8" height="24" fill="var(--sp-fg)" transform="rotate(18 326 212)" />
                  </g>
                )}
                <text x="24" y="34" fill="var(--sp-faint)" fontFamily="JetBrains Mono, monospace" fontSize="10" letterSpacing="2">
                  06:00
                </text>
                <text x="552" y="34" fill="var(--sp-faint)" fontFamily="JetBrains Mono, monospace" fontSize="10" letterSpacing="2">
                  18:00
                </text>
              </svg>
              <p
                data-testid="simulator-drag-hint"
                className="pointer-events-none absolute left-1/2 top-1 -translate-x-1/2 font-mono2 text-[10px] uppercase tracking-[0.14em] text-[var(--sp-faint)]"
              >
                Drag the sun · or use the slider
              </p>
              {auto && (
                <div
                  data-testid="sim-autoplay-badge"
                  className="pointer-events-none absolute right-2 top-2 flex items-center gap-1.5 border border-[var(--sun)] bg-[var(--sp-bg)] px-2 py-1 font-mono2 text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--sun)]"
                >
                  <span className="sp-blink h-1.5 w-1.5 bg-[var(--sun)]" />
                  Autoplay
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 border-t border-[var(--sp-line)] px-5 py-6 lg:grid-cols-2 lg:items-center">
            <div>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <button
                  type="button"
                  data-testid="sim-autoplay-toggle"
                  aria-pressed={auto}
                  onClick={() => setAuto((a) => !a)}
                  className={`flex items-center gap-1.5 border px-2.5 py-1.5 font-mono2 text-[10px] font-bold uppercase tracking-[0.14em] transition-colors duration-300 ${
                    auto
                      ? "border-[var(--sun)] text-[var(--sun)]"
                      : "border-[var(--sp-line)] text-[var(--sp-muted)] hover:border-[var(--sun)] hover:text-[var(--sun)]"
                  }`}
                >
                  {auto ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                  {auto ? "Autoplay On" : "Autoplay Off"}
                </button>
                <span className="font-mono2 text-[10px] uppercase tracking-[0.14em] text-[var(--sp-muted)]">
                  Solar Time{" "}
                  <span data-testid="solar-time-readout" className="ml-1 font-bold text-[var(--sun)]">
                    {fmt}
                  </span>
                </span>
              </div>
              <input
                id="solar-time-slider"
                data-testid="solar-time-slider"
                type="range"
                min="6"
                max="18"
                step="0.5"
                value={hour}
                aria-label="Simulated solar time"
                onChange={(e) => {
                  setAuto(false);
                  setHour(parseFloat(e.target.value));
                }}
                className="sp-range w-full"
              />
              <p
                data-testid="sim-time-note"
                className="mt-2 font-mono2 text-[10px] font-bold uppercase tracking-[0.1em] text-[var(--sp-faint)]"
              >
                Times shown are for the simulation, <span className="font-bold text-[var(--sp-muted)]">not your local time</span>
              </p>
            </div>
            <div className="grid grid-cols-3 divide-x divide-[var(--sp-line)] border border-[var(--sp-line)]">
              {[
                ["Solar Elev", `${elev}°`, "var(--sun)"],
                ["Shadow Length", `${Math.round(shadowLen / 10)}m`, accent],
                [seeking === "shade" ? "Shade Match" : "Sun Match", `${comfort}%`, accent],
              ].map(([k, v, c]) => (
                <div key={k} className="px-4 py-3 text-center">
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.1em] text-[var(--sp-faint)]">{k}</p>
                  <p data-testid={`sim-metric-${k.toLowerCase().replace(/\s/g, "-")}`} style={{ color: c }} className="mt-1 font-mono2 text-sm font-bold">
                    {v}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <p
            data-testid="sim-accuracy-note"
            className="border-t border-[var(--sp-line)] px-5 py-4 font-mono2 text-[10px] uppercase leading-relaxed tracking-[0.1em] text-[var(--sp-faint)]"
          >
            Accuracy note: this is a simplified illustrative model, not a geolocated forecast. It assumes a fixed 06:00–18:00 day with a symmetric sun arc peaking at 68°, and ignores your latitude, date, season, terrain and weather. Real shade in the app is crowdsourced from on-the-ground reports.
          </p>

        </motion.div>
      </div>
    </section>
  );
}
